import { ReusableTooltip } from '@/components/ui/ReusableTooltip';
import { SkeletonLoader } from '@/components/ui/Skeleton';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/trpc/react';
import Link from 'next/link';
import { useState } from 'react';
import type { CitationAttributes } from './citation-extension';
import { makeUrlsClickable } from './citations-item';

export const CitationTooltip = ({
  content,
  children,
  align,
  id,
  citation,
  delay = 300,
}: {
  content: string;
  children: React.ReactNode;
  citation: CitationAttributes['citation'];
  align?: 'start' | 'center' | 'end';
  delay?: number;
  id: string;
}) => {
  const [open, setOpen] = useState(false);

  const { user } = useAuth();

  const { data, isPending } = api.citations.format.useQuery(
    {
      citation,
      format: user?.citationFormat ?? 'APA_7',
    },
    {
      enabled: open,
    },
  );

  return (
    <ReusableTooltip
      onOpenChange={(open) => setOpen(open)}
      contentProps={{
        onPointerDownOutside: (event) => {
          event.preventDefault();
        },
      }}
      delayDuration={delay}
      triggerProps={{
        onClick: (event) => {
          event.preventDefault();
        },
      }}
      align={align}
      content={
        isPending ? (
          <div className="flex items-start flex-col gap-2.5 w-72">
            <div className="flex flex-row gap-1.5">
              <SkeletonLoader className="w-14 h-4" />
              <SkeletonLoader className="w-9 h-4" />
              <SkeletonLoader className="w-6 h-4" />
              <SkeletonLoader className="w-10 h-4" />
              <SkeletonLoader className="w-16 h-4" />
              <SkeletonLoader className="w-6 h-4" />
            </div>
            <div className="flex flex-row gap-2">
              <SkeletonLoader className="w-6 h-4" />
              <SkeletonLoader className="w-8 h-4" />
              <SkeletonLoader className="w-5 h-4" />
              <SkeletonLoader className="w-12 h-4" />
              <SkeletonLoader className="w-14 h-4" />
              <SkeletonLoader className="w-[60px] h-4" />
            </div>
            <div className="flex flex-row gap-2">
              <SkeletonLoader className="w-8 h-4" />
              <SkeletonLoader className="w-16 h-4" />
              <SkeletonLoader className="w-6 h-4" />
              <SkeletonLoader className="w-14 h-4" />
              <SkeletonLoader className="w-10 h-4" />
            </div>
          </div>
        ) : (
          <Link href={`#${id}`} target="_self">
            <div className="line-clamp-[7] text-foreground dark:text-muted-foreground2">
              <p>
                {open && data ? (
                  <span
                    className="prose text-sm text-foreground prose-a:text-foreground prose-a:whitespace-pre-wrap break-words"
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                    dangerouslySetInnerHTML={{
                      __html: makeUrlsClickable(data),
                    }}
                  />
                ) : (
                  content
                )}
              </p>
            </div>
          </Link>
        )
      }
      className="bg-popover max-w-[300px] border border-border px-3 py-2.5 shadow-feint text-[13px] leading-[19.5px] -tracking-[0.32px]"
    >
      {children}
    </ReusableTooltip>
  );
};
